// ─────────────────────────────────────────────────────────────────────────
// financeApprovalConfig — declarative behaviour for the config-driven Finance
// Approval flow. One record here per approval TYPE; the CONTRACT type (W2 /
// C2C) is a data flag off the record (see financeApprovalEngine) that only
// swaps the employer-side deduction row, so it is NOT a separate config.
//
// FinanceApprovalPanel reads exactly this shape and renders/permits nothing
// that isn't declared here, so new stages or per-tenant tweaks are a config
// edit, not a component change.
//
// action.api values are resolved by the HOST app (ZINNEXT-V2) to real API
// calls — CORE-THEME stays backend-agnostic (window.__CORE_THEME_CONFIG__
// pattern). The panel receives an `actions` prop mapping api-key → handler.
// ─────────────────────────────────────────────────────────────────────────

export const FINANCE_TYPE_SUBMISSION_MANAGER = 'submissionManager';
export const FINANCE_TYPE_ONBOARDING_MANAGER = 'onboardingManager';
export const FINANCE_TYPE_CFO = 'cfo';
export const FINANCE_TYPE_ONLY_FINANCE = 'onlyFinance';
// Re-approval stages — entered when the CFO REJECTS. Identical to their base
// stage except the CFO's rejection reason is surfaced at the top so the
// manager knows what to change before re-submitting.
export const FINANCE_TYPE_SUBMISSION_MANAGER_REAPPROVE = 'submissionManagerReapprove';
export const FINANCE_TYPE_ONBOARDING_MANAGER_REAPPROVE = 'onboardingManagerReapprove';

// ─────────────────────────────────────────────────────────────────────────
// FINANCE_DEFAULTS — everything the panel used to hardcode, declared once and
// merged into every type by getFinanceApprovalConfig(). A stage overrides only
// what it wants to differ; nothing has to be repeated per type.
//
// `source` arrays are dotted paths resolved against { record, identity, inputs,
// computed } in the panel, first non-empty wins. That is what makes the Basic
// Details grid and the header crumb DATA rather than JSX: adding, removing or
// reordering a field is a config edit.
// ─────────────────────────────────────────────────────────────────────────
// fillTokens — substitutes {name} placeholders in an admin-configured string.
// Warning and confirm copy on the approval surface is config (see
// sections.rateBreakdown.proposed.overClient), so the live numbers have to be
// injected rather than concatenated in the JSX — that is what keeps the wording
// editable without a deploy. An unknown token is left INTACT so a typo in
// config shows up as "{propsed}" instead of silently blanking the sentence.
export function fillTokens(template, tokens = {}) {
  if (!template) return '';
  return String(template).replace(
    /\{(\w+)\}/g,
    (match, key) => (tokens[key] === undefined ? match : String(tokens[key])),
  );
}

export const FINANCE_DEFAULTS = {
  // Tail of the engine's currency/unit resolution chains (see
  // resolveFinanceInputs) — used only when the record itself carries neither.
  rateUnit: 'hr',
  currency: '$',

  labels: {
    // A single 0% employer VMS has two very different meanings and the panel
    // must not conflate them (see engine: employerVmsAvailable).
    employerVms: {
      required: 'Employer VMS',
      notRequired: 'Not required',
      notRequiredSub: 'VMS not required for this submission',
      unavailable: 'VMS Not available',
      unavailableSub: 'No VMS percentage configured for this employer',
    },
  },

  header: {
    // The crumb above the title. Because it already carries the client and the
    // job reference, those two are deliberately ABSENT from
    // sections.jobClient.items below — they were being printed twice.
    crumbSeparator: ' · ',
    crumbFields: [
      { key: 'client', source: ['record.client.clientName', 'identity.client'] },
      { key: 'reqId', source: ['record.job.jobReferenceId', 'identity.reqId'] },
    ],
  },

  sections: {
    jobClient: {
      title: 'Basic Details',
      items: [
        { key: 'jobTitle', label: 'Job Title', source: ['record.job.jobTitle', 'identity.role'] },
        { key: 'jobLocation', label: 'Job Location', source: ['identity.location', 'record.job.jobLocation'] },
        { key: 'candidateName', label: 'Candidate Name', source: ['identity.name', 'record.submission.candidateFullName'] },
        { key: 'candidateEmail', label: 'Candidate Email', source: ['identity.email', 'record.submission.email'] },
        { key: 'contractType', label: 'Employment Type', source: ['inputs.contractType'] },
        { key: 'employer', label: 'Employer', source: ['record.submission.supplierName', 'identity.vendor'] },
        { key: 'submittedBy', label: 'Submitted By', source: ['identity.submittedBy', 'record.submission.signatureEmail'] },
        {
          key: 'submittedAt',
          label: 'Submitted Date',
          source: ['identity.submittedAt', 'record.submission.createdAt'],
          format: 'date',
        },
      ],
    },
    rateBreakdown: {
      // Numeric policy for the editable proposed rate. decimals:2 keeps
      // "62.50" valid and rejects a 3rd decimal at the keystroke; setting
      // decimals:0 here makes the field integer-only with NO code change.
      proposed: {
        policy: {
          decimals: 2,
          min: 0,
          message: 'Enter a valid amount — numbers only, up to 2 decimal places.',
        },
        // What happens when the proposed (client-facing) rate is ABOVE the
        // Client Job Rate. This used to be an unconditional hard block, which
        // conflated two very different situations: "no rate entered at all"
        // (genuinely blocking — the margin, the CFO request and payroll are all
        // derived from it) and "the rate is higher than the client's posted
        // range", which is a commercial judgement the approver is entitled to
        // make. Only the first still blocks.
        //   'warn'   → show the comparison panel, approval stays available
        //              behind a deliberate confirm step (default)
        //   'block'  → the previous behaviour, restorable with no code change
        //   'ignore' → no panel at all
        overClientPolicy: 'warn',
        overClient: {
          title: 'Proposed rate is above the Client Job Rate',
          // {proposed} {client} {delta} {pct} {unit} are substituted by the
          // panel; keeping the copy here is what stops it becoming a literal
          // in the JSX again.
          body: 'You are asking the client for {delta} ({pct}) more than the rate posted on this job. Approving is allowed — the higher rate will be what goes to the client and what payroll is finalised against.',
          confirmTitle: 'Approve above the client rate?',
          confirmBody: 'The proposed rate of {proposed} exceeds the Client Job Rate of {client} by {delta} ({pct}). This will be recorded against your approval.',
          confirmOk: 'Approve anyway',
          confirmCancel: 'Go back',
          // Used only when overClientPolicy is 'block'.
          blockMessage: 'Proposed rate can’t exceed the Client Job Rate ({client}).',
        },
      },
    },
    // The referral rate deliberately reuses the SAME policy object shape, so
    // both money inputs on this surface obey one rule.
    referral: {
      policy: {
        decimals: 2,
        min: 0,
        message: 'Enter a valid amount — numbers only, up to 2 decimal places.',
      },
    },
  },
};

// Shared approval trail — each type marks its own `current` step; earlier
// steps render as completed, later steps are not shown for manager stages.
const TRAIL = [
  { key: 'submitted', label: 'Recruiter Submitted' },
  { key: 'manager', label: 'Manager Approved' },
  { key: 'onboarding', label: 'Onboarding Confirmed' },
  { key: 'cfo', label: 'CFO Review' },
];

export const FINANCE_APPROVAL_CONFIG = {
  // ── Submission-side finance review (Delivery Manager / Approve access) ───
  // Reviews & adjusts the proposed rate, optionally attaches a referral, then
  // approves the submission. Editable proposed + referral rate.
  [FINANCE_TYPE_SUBMISSION_MANAGER]: {
    title: 'Finance Review',
    statusLabel: 'Pending Your Approval',
    trail: { steps: TRAIL.slice(0, 2), current: 'manager', show: false },
    sections: {
      jobClient: { show: true },
      clientJobRate: { show: true, note: true },
      rateBreakdown: {
        show: true,
        employer: { show: true, editable: false },
        proposed: { show: true, editable: true }, // ← Delivery Manager's rate input
      },
      // Delivery Manager (Approve access on Submissions) may also attach a
      // referral at this stage — same shape as onboardingManager's referral
      // card, so it reuses the panel's existing "select" rendering as-is.
      referral: {
        show: true,
        toggle: true,
        toggleLabel: 'Add Referral',
        toggleHint: 'Include a referral payout — reduces the margin below.',
        mode: 'select',
        source: 'referrals',
        optionLabelField: 'referalCompanyName',
        rateEditable: true,
        typeField: true,
        lastRate: true,
        linkBothWays: true,
      },
    },
    margin: {
      label: 'Realtek Margin',
      formula: 'netProposed-netEmployer-referral',
    },
    actions: {
      // requireProposedRate — the proposed rate is the ONE input on this stage
      // and everything downstream (margin, CFO sign-off, payroll) is derived
      // from it, so approving with it blank/0 is never valid. The panel
      // disables the button and explains why on hover.
      primary: {
        key: 'approve', label: 'Approve', tone: 'primary', api: 'submissionApprove', requireProposedRate: true,
      },
      // Reject here does NOT collect an inline reason — clicking it hands off
      // to the dedicated Reject popup (reason dropdown + comments). `openPopup`
      // is the marker the host body reads to open that popup; the panel skips
      // the inline reason box because `requireReason` is absent.
      reject: { key: 'reject', label: 'Reject', tone: 'warn', api: 'submissionReject', openPopup: 'reject' },
    },
    // save the edited proposed rate + referral into submissions.proposedRate
    // history / the linked referral, then run native approve (decision 1 =
    // "Save rate + native approve").
    persist: { proposedRateHistory: true, referralLink: true, rateHistory: true },
    readOnly: false,
  },

  // ── Onboarding-side finance review (MSI / Approve+ReApprove access) ─────
  // Manager may edit the proposed rate and attach a referral (dropdown from
  // referrals.referalCompanyName) + referral rate, then send for CFO
  // approval. `editToggle` renders both as a locked preview by default with
  // an Edit button — see FinanceApprovalPanel's ratesEditing state.
  [FINANCE_TYPE_ONBOARDING_MANAGER]: {
    title: 'Onboarding Review',
    statusLabel: 'Pending Onboarding',
    trail: { steps: TRAIL.slice(0, 3), current: 'onboarding', show: true },
    sections: {
      jobClient: { show: true },
      clientJobRate: { show: true, note: true },
      rateBreakdown: {
        show: true,
        editToggle: true, // preview-first UX: Edit button reveals the inputs below
        employer: { show: true, editable: false, locked: true },
        proposed: { show: true, editable: true },
      },
      referral: {
        show: true,
        toggle: true, // "Add Referral" checkbox gates the whole referral card
        toggleLabel: 'Add Referral',
        toggleHint: 'Include a referral payout — reduces the total margin below.',
        mode: 'select', // dropdown + rate input
        source: 'referrals',
        optionLabelField: 'referalCompanyName',
        rateEditable: true,
        typeField: true, // Recurring | One-time
        lastRate: true, // show last paid referalRate for the picked referral
        linkBothWays: true, // referralId→engagement, engagementId→referral (decision 3)
      },
    },
    margin: {
      label: 'Total Margin',
      formula: 'netProposed-netEmployer-referral',
    },
    actions: {
      primary: { key: 'send', label: 'Send for CFO Approval', tone: 'primary', api: 'cfoSend' },
      reject: { key: 'reject', label: 'Reject', tone: 'warn', api: 'onboardingReject', requireReason: true },
    },
    persist: { proposedRateHistory: true, referralLink: true, referralRateOnEngagement: true, rateHistory: true },
    readOnly: false,
  },

  // ── CFO ─────────────────────────────────────────────────────────────────
  // Everything printed — no typeable fields. Referral shows only if one was
  // attached upstream. Approve → finalize (advances to PO_MSA); reject needs
  // only a reason — no "Return To" category. Whoever holds the ReApprove
  // permission on Onboarding resends after a reject, regardless of what
  // triggered it (access-controlled downstream, not routed by category).
  [FINANCE_TYPE_CFO]: {
    title: 'Final Approval',
    statusLabel: 'Pending CFO Sign-off',
    trail: { steps: TRAIL, current: 'cfo', show: true },
    sections: {
      jobClient: { show: true },
      clientJobRate: { show: true, note: true },
      rateBreakdown: {
        show: true,
        finalBadge: true,
        employer: { show: true, editable: false },
        proposed: { show: true, editable: false },
      },
      referral: {
        // Printed ONLY when the onboarding manager actually enabled a referral
        // (their checkbox) — otherwise the card and its margin term disappear.
        show: 'ifEnabled',
        mode: 'print',
        typeChip: true,
      },
    },
    margin: {
      label: 'Total Margin',
      formula: 'netProposed-netEmployer-referral',
    },
    actions: {
      primary: { key: 'approve', label: 'Approve & Finalize', tone: 'primary', api: 'cfoApprove' },
      reject: {
        key: 'reject', label: 'Reject', tone: 'warn', api: 'cfoReject', requireReason: true,
      },
    },
    persist: { rateHistory: false }, // CFO decision only; rates already logged
    readOnly: true, // no inputs anywhere
  },

  // ── Read-only finance summary ───────────────────────────────────────────
  // Page/detail-view surface: only the rate breakdown, an existing referral
  // (when one was already attached upstream), and the resulting total margin.
  // It never exposes referral controls or approval actions.
  [FINANCE_TYPE_ONLY_FINANCE]: {
    title: 'Finance Summary',
    statusLabel: '',
    header: { show: false },
    trail: { steps: [], current: '', show: false },
    sections: {
      jobClient: { show: false },
      clientJobRate: { show: false },
      rateBreakdown: {
        show: true,
        finalBadge: true,
        employer: { show: true, editable: false },
        proposed: { show: true, editable: false, locked: true },
      },
      referral: {
        show: 'ifExists',
        mode: 'print',
        typeChip: true,
      },
    },
    margin: {
      label: 'Total Margin',
      formula: 'netProposed-netEmployer-referral',
    },
    actions: { show: false },
    persist: { rateHistory: false },
    readOnly: true,
  },
};

// ── Re-approval stages (CFO rejected → back to the manager) ────────────────
// Same surface as the base stage, plus the rejection banner. Built by
// derivation so the two stay in lockstep with their base config.
function withRejection(base, { title, primaryLabel, primaryApi }) {
  return {
    ...base,
    title,
    statusLabel: 'Rejected by CFO — Re-approval Needed',
    rejection: {
      show: true,
      // where the reason lives on the record (checked in order). The
      // submissionManager surface has no `engagement` object resolved (it's a
      // plain submission review, not an engagement one) — its reason instead
      // comes from the submission's own activity log: the last
      // submissionActivity entry with activityType "CFO Rejected" (see
      // workforceCFOApprovalController.go), attached by the manager-reapprove
      // popup onto record.submission.cfoRejectionReason before the panel ever
      // sees it. Harmless to check first for the onboarding surface too — it
      // just won't have that path and falls through to the next source.
      sources: [
        'submission.cfoRejectionReason',
        'engagement.financeApproval.rejectionReason',
        'engagement.financeApproval.remarks',
        'financeApprovalRequest.rejectionReason',
        'rejectionReason',
      ],
      title: 'CFO Rejected This Request',
    },
    // A re-approval surface is a "fix and resend" step — it offers ONLY its
    // forward action (Re-Approve / Re-send), never a reject. Dropping
    // base.actions.reject means the panel renders no reject button here.
    actions: {
      primary: {
        ...base.actions.primary,
        label: primaryLabel,
        // A re-approval may target a different handler than its base stage —
        // e.g. the submission re-approval both re-approves AND re-sends to the
        // CFO, so it uses its own combined api. Falls back to the base api.
        ...(primaryApi ? { api: primaryApi } : {}),
      },
    },
  };
}

FINANCE_APPROVAL_CONFIG[FINANCE_TYPE_SUBMISSION_MANAGER_REAPPROVE] = {
  ...withRejection(
    FINANCE_APPROVAL_CONFIG[FINANCE_TYPE_SUBMISSION_MANAGER],
    // "Send For Approval" (not "Re-Approve"): after a CFO rate rejection the
    // submission manager fixes the rate and re-sends it for CFO review — the
    // handler re-approves the submission AND re-submits the engagement to the CFO.
    { title: 'Finance Re-Review', primaryLabel: 'Send For Approval', primaryApi: 'submissionReapproveSend' },
  ),
  // The onboarding team may already have attached a referral before the CFO
  // rejection. Show that saved referral as read-only during manager re-review
  // and include its rate in the recalculated margin.
  sections: {
    ...FINANCE_APPROVAL_CONFIG[FINANCE_TYPE_SUBMISSION_MANAGER].sections,
    referral: {
      show: 'ifExists',
      mode: 'print',
      typeChip: true,
    },
  },
  margin: {
    label: 'Total Margin',
    formula: 'netProposed-netEmployer-referral',
  },
};

FINANCE_APPROVAL_CONFIG[FINANCE_TYPE_ONBOARDING_MANAGER_REAPPROVE] = withRejection(
  FINANCE_APPROVAL_CONFIG[FINANCE_TYPE_ONBOARDING_MANAGER],
  { title: 'Onboarding Re-Review', primaryLabel: 'Re-send for CFO Approval' },
);

// withDefaults — fill in anything a type did not declare from FINANCE_DEFAULTS.
// Plain objects merge key-wise (so `sections.rateBreakdown.proposed` keeps its
// own show/editable/locked AND gains the default policy); everything else,
// arrays included, is taken WHOLE from the type when present — a stage that
// declares its own `items` means exactly that list, not that list appended to
// the defaults.
const isPlainObject = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);

function withDefaults(value, defaults) {
  if (!isPlainObject(defaults)) return value === undefined ? defaults : value;
  if (value === undefined) return defaults;
  if (!isPlainObject(value)) return value;
  const out = { ...value };
  for (const [key, fallback] of Object.entries(defaults)) {
    out[key] = withDefaults(value[key], fallback);
  }
  return out;
}

// The merged configs are CACHED per type: the panel holds `config` in useMemo
// dependency arrays, so returning a freshly built object on every render would
// re-run the finance computation on every keystroke for no reason.
const resolvedConfigCache = new Map();

// getFinanceApprovalConfig — safe accessor. Unknown type falls back to the
// most restrictive (cfo / read-only) rather than throwing, so a misconfigured
// host never exposes an editable panel by accident.
export function getFinanceApprovalConfig(type) {
  const base = FINANCE_APPROVAL_CONFIG[type] ?? FINANCE_APPROVAL_CONFIG[FINANCE_TYPE_CFO];
  if (!resolvedConfigCache.has(base)) resolvedConfigCache.set(base, withDefaults(base, FINANCE_DEFAULTS));
  return resolvedConfigCache.get(base);
}

// resolveRejectionReason — first non-empty value from the config's source paths.
export function resolveRejectionReason(config, record) {
  const paths = config?.rejection?.sources ?? [];
  for (const path of paths) {
    const value = path.split('.').reduce((acc, key) => acc?.[key], record);
    if (value !== undefined && value !== null && String(value).trim() !== '') return String(value);
  }
  return '';
}

// marginIncludesReferral — the margin formula string is the single source of
// truth for whether a stage folds the referral into the outcome.
export function marginIncludesReferral(config) {
  return String(config?.margin?.formula ?? '').includes('referral');
}
